import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faCartPlus,
    faDollarSign,
    faClock,
    faCheckCircle,
    faSearch,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import Footer from "../Footer";
import Sidebar from "../sidebar";
import Topnav from "../TopNav";
import { API_URL, token } from "../config";
import Swal from "sweetalert2";

const Admin = () => {
    const [stats, setStats] = useState({
        total_purchases: 0,
        total_revenue: 0,
        pending_withdrawals: 0,
        cleared_withdrawals: 0,
        total_authors: 0
    });
    const [purchases, setPurchases] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const toggleSidebar = () => {
        setShow(prevShow => !prevShow);
    };

    const fetchAdminData = async () => {
        setLoading(true);
        try {
            // Fetch stats
            const statsRes = await fetch(`${API_URL}/authors/admin/stats`, {
                headers: { 'Authorization': `Bearer ${token()}` }
            });
            const statsData = await statsRes.json();
            if (statsRes.ok) setStats(statsData);

            // Fetch all purchases
            const purchasesRes = await fetch(`${API_URL}/authors/admin/purchases`, {
                headers: { 'Authorization': `Bearer ${token()}` }
            });
            const purchasesData = await purchasesRes.json();
            if (purchasesRes.ok) setPurchases(purchasesData.purchases || []);

            // Fetch all withdrawals
            const withdrawalsRes = await fetch(`${API_URL}/authors/admin/withdrawals`, {
                headers: { 'Authorization': `Bearer ${token()}` }
            });
            const withdrawalsData = await withdrawalsRes.json();
            if (withdrawalsRes.ok) setWithdrawals(withdrawalsData.withdrawals || []);

            // Fetch all authors (for search)
            const authorsRes = await fetch(`${API_URL}/authors/admin/authors`, {
                headers: { 'Authorization': `Bearer ${token()}` }
            });
            const authorsData = await authorsRes.json();
            if (authorsRes.ok) setAuthors(authorsData.authors || []);
        } catch (error) {
            console.error('Error fetching admin data:', error);
            Swal.fire({ text: "Failed to load admin data", icon: "error" });
        }
        setLoading(false);
    };

    useEffect(() => {
        // Check if user is admin
        const storedAuthor = localStorage.getItem('author');
        if (storedAuthor) {
            const author = JSON.parse(storedAuthor);
            if (!author.is_admin) {
                window.location.href = '/dashboard'; // redirect non-admin
                return;
            }
        } else {
            window.location.href = '/';
            return;
        }
        fetchAdminData();
    }, []);

    const handleClearWithdrawal = async (withdrawalId) => {
        try {
            const response = await fetch(`${API_URL}/authors/admin/withdrawals/${withdrawalId}/clear`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token()}` }
            });
            const data = await response.json();
            if (response.ok) {
                Swal.fire({ text: "Withdrawal cleared!", icon: "success" });
                fetchAdminData(); // refresh
            } else {
                Swal.fire({ text: data.error || "Failed to clear", icon: "error" });
            }
        } catch (error) {
            console.error('Clear withdrawal error:', error);
            Swal.fire({ text: "An error occurred", icon: "error" });
        }
    };

    const searchAuthor = async (authorId) => {
        if (!authorId) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/authors/admin/author/${authorId}`, {
                headers: { 'Authorization': `Bearer ${token()}` }
            });
            const data = await response.json();
            if (response.ok) {
                setSelectedAuthor(data.author);
            } else {
                Swal.fire({ text: data.error || "Author not found", icon: "error" });
            }
        } catch (error) {
            console.error('Search author error:', error);
            Swal.fire({ text: "An error occurred", icon: "error" });
        }
        setLoading(false);
    };

    const filteredAuthors = authors.filter(a =>
        a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <html lang="en">
            <body>
                <div className="wrapper">
                    <Sidebar show={show} />
                    <div className="main-panel">
                        <Topnav toggleSidebar={toggleSidebar} />

                        <div className="container">
                            <div className="page-inner">
                                <h3 className="fw-bold mb-3">Admin Dashboard</h3>

                                {/* Stats Cards */}
                                <div className="row">
                                    <div className="col-sm-6 col-md-3">
                                        <div className="card card-stats card-round">
                                            <div className="card-body">
                                                <div className="row align-items-center">
                                                    <div className="col-icon">
                                                        <div className="icon-big text-center icon-primary bubble-shadow-small">
                                                            <FontAwesomeIcon icon={faCartPlus} style={{ fontSize: '2.5rem', color: '#fff' }} />
                                                        </div>
                                                    </div>
                                                    <div className="col col-stats ms-3 ms-sm-0">
                                                        <div className="numbers">
                                                            <p className="card-category">Total Purchases</p>
                                                            <h4 className="card-title">{stats.total_purchases || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6 col-md-3">
                                        <div className="card card-stats card-round">
                                            <div className="card-body">
                                                <div className="row align-items-center">
                                                    <div className="col-icon">
                                                        <div className="icon-big text-center icon-secondary bubble-shadow-small">
                                                            <FontAwesomeIcon icon={faDollarSign} style={{ fontSize: '2.5rem', color: '#fff' }} />
                                                        </div>
                                                    </div>
                                                    <div className="col col-stats ms-3 ms-sm-0">
                                                        <div className="numbers">
                                                            <p className="card-category">Total Revenue</p>
                                                            <h4 className="card-title">${stats.total_revenue || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6 col-md-3">
                                        <div className="card card-stats card-round">
                                            <div className="card-body">
                                                <div className="row align-items-center">
                                                    <div className="col-icon">
                                                        <div className="icon-big text-center icon-warning bubble-shadow-small">
                                                            <FontAwesomeIcon icon={faClock} style={{ fontSize: '2.5rem', color: '#fff' }} />
                                                        </div>
                                                    </div>
                                                    <div className="col col-stats ms-3 ms-sm-0">
                                                        <div className="numbers">
                                                            <p className="card-category">Pending Withdrawals</p>
                                                            <h4 className="card-title">${stats.pending_withdrawals || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6 col-md-3">
                                        <div className="card card-stats card-round">
                                            <div className="card-body">
                                                <div className="row align-items-center">
                                                    <div className="col-icon">
                                                        <div className="icon-big text-center icon-success bubble-shadow-small">
                                                            <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '2.5rem', color: '#fff' }} />
                                                        </div>
                                                    </div>
                                                    <div className="col col-stats ms-3 ms-sm-0">
                                                        <div className="numbers">
                                                            <p className="card-category">Cleared Withdrawals</p>
                                                            <h4 className="card-title">${stats.cleared_withdrawals || 0}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Search Author */}
                                <div className="row mt-4">
                                    <div className="col-md-12">
                                        <div className="card">
                                            <div className="card-header">
                                                <h5><FontAwesomeIcon icon={faSearch} /> Search Author</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-lg-8">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search by name or email..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-lg-4">
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={() => {
                                                                if (filteredAuthors.length === 1) {
                                                                    searchAuthor(filteredAuthors[0].id);
                                                                } else if (filteredAuthors.length > 1) {
                                                                    Swal.fire({
                                                                        title: 'Multiple authors found',
                                                                        text: 'Please select one from the list below.',
                                                                        icon: 'info'
                                                                    });
                                                                } else {
                                                                    Swal.fire({ text: 'No author found', icon: 'warning' });
                                                                }
                                                            }}
                                                        >
                                                            Search
                                                        </button>
                                                    </div>
                                                </div>
                                                {filteredAuthors.length > 1 && (
                                                    <div className="mt-3">
                                                        <h6>Select an author:</h6>
                                                        <ul className="list-group">
                                                            {filteredAuthors.slice(0, 10).map(a => (
                                                                <li key={a.id} className="list-group-item list-group-item-action" style={{ cursor: 'pointer' }} onClick={() => searchAuthor(a.id)}>
                                                                    {a.name} ({a.email})
                                                                </li>
                                                            ))}
                                                            {filteredAuthors.length > 10 && <li className="list-group-item">... and more</li>}
                                                        </ul>
                                                    </div>
                                                )}
                                                {selectedAuthor && (
                                                    <div className="mt-4">
                                                        <h6>Author Details: {selectedAuthor.name}</h6>
                                                        <p><strong>Email:</strong> {selectedAuthor.email}</p>
                                                        <p><strong>Balance:</strong> ${selectedAuthor.balance || 0}</p>
                                                        <p><strong>Total Sales:</strong> {selectedAuthor.purchases?.length || 0}</p>
                                                        <p><strong>Total Withdrawals:</strong> {selectedAuthor.withdrawals?.length || 0}</p>
                                                        <button className="btn btn-secondary" onClick={() => setSelectedAuthor(null)}>
                                                            <FontAwesomeIcon icon={faTimes} /> Clear
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Withdrawals Table */}
                                <div className="row mt-4">
                                    <div className="col-md-12">
                                        <h4>Withdrawals</h4>
                                        <div className="card">
                                            <div className="table-responsive pt-3">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Author</th>
                                                            <th>Amount</th>
                                                            <th>Status</th>
                                                            <th>Requested</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {withdrawals.map((w) => (
                                                            <tr key={w.id}>
                                                                <td>{w.author_name} ({w.author_email})</td>
                                                                <td>${w.amount}</td>
                                                                <td>
                                                                    <span className={`badge ${w.status === 'cleared' ? 'bg-success' : 'bg-warning'}`}>
                                                                        {w.status}
                                                                    </span>
                                                                </td>
                                                                <td>{new Date(w.requested_at).toLocaleDateString()}</td>
                                                                <td>
                                                                    {w.status === 'pending' && (
                                                                        <button
                                                                            className="btn btn-sm btn-primary"
                                                                            onClick={() => handleClearWithdrawal(w.id)}
                                                                        >
                                                                            Clear
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {withdrawals.length === 0 && (
                                                            <tr><td colSpan="5" className="text-center">No withdrawals</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Purchases Table */}
                                <div className="row mt-4">
                                    <div className="col-md-12">
                                        <h4>All Purchases</h4>
                                        <div className="card">
                                            <div className="table-responsive pt-3">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Author</th>
                                                            <th>Book ID</th>
                                                            <th>Email</th>
                                                            <th>Amount</th>
                                                            <th>Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {purchases.map((p) => (
                                                            <tr key={p.id}>
                                                                <td>{p.author_name}</td>
                                                                <td>{p.book_id}</td>
                                                                <td>{p.email}</td>
                                                                <td>${p.amount}</td>
                                                                <td>{new Date(p.purchase_date).toLocaleDateString()}</td>
                                                            </tr>
                                                        ))}
                                                        {purchases.length === 0 && (
                                                            <tr><td colSpan="5" className="text-center">No purchases</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <footer className="footer">
                            <Footer />
                        </footer>
                    </div>
                </div>
            </body>
        </html>
    );
};

export default Admin;